import { secureCache } from '~/utils/secureCache'

const SIGNUP_DRAFT_FIELDS = [
  'signupType',
  'insurancePayerId',
  'firstName',
  'lastName',
  'preferredName',
  'dob',
  'consentForms',
  'inviteCode',
  'sex',
  'gender',
  'pronouns',
  'preferredLanguage',
  'preferredLanguageOther',
  'state',
  'termsAndConditions',
] as const

type SignupDraft = Record<string, string>

const draftKey = (email: string) => `signupDraft:${email.toLowerCase()}`

const getSignupDraft = (email: string): SignupDraft | null => {
  const raw = secureCache.get(draftKey(email))
  if (!raw) return null
  try {
    return JSON.parse(raw) as SignupDraft
  } catch {
    return null
  }
}

export const hasSignupDraft = (email: string): boolean => getSignupDraft(email) !== null

/**
 * Consolidate the flat signup keys into a per-email draft (once) and clear the
 * flat keys. No-op that returns the existing draft when one is already keyed to
 * this email (the re-entry case). Returns `null` when there's nothing to key —
 * e.g. an authenticated user who is not mid-signup.
 */
export const consolidateSignupDraft = (email: string): SignupDraft | null => {
  const existing = getSignupDraft(email)
  if (existing) return existing

  const draft: SignupDraft = {}
  SIGNUP_DRAFT_FIELDS.forEach(field => {
    const value = secureCache.get(field)
    if (value != null) draft[field] = value
  })
  if (Object.keys(draft).length === 0) return null

  secureCache.set(draftKey(email), JSON.stringify(draft))
  SIGNUP_DRAFT_FIELDS.forEach(field => secureCache.remove(field))
  return draft
}

/** Move a draft to a new email key (used when the user changes their email). */
export const renameSignupDraft = (oldEmail: string, newEmail: string): void => {
  const raw = secureCache.get(draftKey(oldEmail))
  if (raw == null) return
  secureCache.set(draftKey(newEmail), raw)
  secureCache.remove(draftKey(oldEmail))
}

/**
 * Drop a draft once its account has been created.
 */
export const clearSignupDraft = (email: string): void => {
  secureCache.remove(draftKey(email))
}
